import { useCallback } from "react";
import { type KeyedMutator } from "swr";

import {
  type RoofMarketplaceBidRequest,
  type UiEvents,
} from "@keystone-ui/types";

import { useKeystone, usePolicyPlatformId, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { trackEvent } from "utils";

export type UseRoofMarketplaceRequestBidsArgs = {
  qualifyingReferralIds: string[];
  /** Fallback policy id for analytics when platform policy id is missing. */
  roofMarketplacePolicyId: string;
  mutateUiEvents: KeyedMutator<UiEvents | undefined>;
};

/** Bid submission for the roof marketplace drawer: POST bid, persist UI events, revalidate SWR. */
export function useRoofMarketplaceRequestBids({
  qualifyingReferralIds,
  roofMarketplacePolicyId,
  mutateUiEvents,
}: UseRoofMarketplaceRequestBidsArgs) {
  const keystone = useKeystone();
  const platformPolicyId = usePolicyPlatformId();
  const user = useUser();

  return useCallback(
    async (payload: RoofMarketplaceBidRequest): Promise<boolean> => {
      if (!platformPolicyId) {
        trackEvent("Roof Marketplace: Bid request errored", {
          policyId: roofMarketplacePolicyId,
          referralId: qualifyingReferralIds[0] ?? "",
          referralIds: qualifyingReferralIds,
          errorMessage: "missing platform policy id for bid request",
        });
        return false;
      }
      const res = await keystone.requestRoofMarketplaceBid(
        platformPolicyId,
        payload,
      );
      if (isError(res)) {
        trackEvent("Roof Marketplace: Bid request errored", {
          policyId: platformPolicyId,
          referralId: qualifyingReferralIds[0] ?? "",
          referralIds: qualifyingReferralIds,
          errorType: res.name,
          errorMessage: res.message,
        });
        return false;
      }
      trackEvent("Roof Marketplace: Bid request submitted", {
        requestId: res.id,
        policyId: platformPolicyId,
        referralId: qualifyingReferralIds[0] ?? "",
        referralIds: qualifyingReferralIds,
      });
      if (user?.userId) {
        await keystone.updateRoofMarketplaceUiEvent(
          user.userId,
          res.id,
          platformPolicyId,
          qualifyingReferralIds,
        );
      }
      await mutateUiEvents().catch(() => undefined);
      return true;
    },
    [
      keystone,
      mutateUiEvents,
      platformPolicyId,
      qualifyingReferralIds,
      roofMarketplacePolicyId,
      user?.userId,
    ],
  );
}
